import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, Menu, LogOut, LayoutDashboard, Package } from "lucide-react";
import { motion } from "framer-motion";
import { CurrencySelector } from "@/components/CurrencySelector";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

const CATS = [
  { slug: "electronics", name: "Electronics" },
  { slug: "wearables", name: "Wearables" },
  { slug: "apparel", name: "Apparel" },
  { slug: "smart-home", name: "Smart Home" },
  { slug: "travel-gear", name: "Travel Gear" },
  { slug: "modern-office", name: "Modern Office" },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center" data-testid="logo-home-link">
      <img src="/logo-clean.png" alt="Drop Worldwide" className="h-12 w-auto object-contain lg:h-14" />
    </Link>
  );
}

function SearchBar({ onSubmit, testid }) {
  const [q, setQ] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(q); }} className="relative w-full">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search trending products…"
        data-testid={testid} className="w-full rounded-full border-white/10 bg-white/5 pl-10 text-sm text-slate-100 placeholder:text-slate-500 focus-visible:ring-[#00F0FF]" />
    </form>
  );
}

export function Header() {
  const { count, setOpen } = useCart();
  const { user, setAuthOpen, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doSearch = (q) => navigate(`/shop?search=${encodeURIComponent(q)}`);

  return (
    <header className="glass-header sticky top-0 z-50 border-b border-white/10">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6 lg:h-20 lg:px-8">
        <div className="lg:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="text-slate-200"><Menu className="h-6 w-6" /></button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 border-r border-white/10 bg-[#0B0D16] text-slate-100">
              <div className="mt-4 space-y-1">
                <Link to="/shop" onClick={() => setMobileOpen(false)} className="block rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-white/5" data-testid="mobile-nav-shop">All Products</Link>
                {CATS.map((c) => (
                  <Link key={c.slug} to={`/shop?category=${c.slug}`} onClick={() => setMobileOpen(false)} className="block rounded-lg px-3 py-2.5 text-sm hover:bg-white/5" data-testid={`mobile-nav-${c.slug}`}>{c.name}</Link>
                ))}
                <div className="my-2 h-px bg-white/10" />
                <Link to="/track" onClick={() => setMobileOpen(false)} className="block rounded-lg px-3 py-2.5 text-sm hover:bg-white/5">Track Order</Link>
                <Link to="/faq" onClick={() => setMobileOpen(false)} className="block rounded-lg px-3 py-2.5 text-sm hover:bg-white/5">FAQ & Support</Link>
                {["owner", "admin", "manager"].includes(user?.role) && <Link to="/admin" onClick={() => setMobileOpen(false)} className="block rounded-lg px-3 py-2.5 text-sm font-bold text-[#8A2BE2] hover:bg-white/5" data-testid="mobile-nav-admin">Admin Panel</Link>}
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <Logo />

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          <Link to="/shop" className="rounded-full px-3 py-2 text-sm font-medium text-slate-200 transition-colors hover:text-[#00F0FF]" data-testid="nav-shop">Shop</Link>
          <DropdownMenu>
            <DropdownMenuTrigger className="rounded-full px-3 py-2 text-sm font-medium text-slate-200 outline-none transition-colors hover:text-[#00F0FF]" data-testid="nav-categories">Categories</DropdownMenuTrigger>
            <DropdownMenuContent className="border-white/10 bg-[#111420] text-slate-200">
              {CATS.map((c) => (
                <DropdownMenuItem key={c.slug} onClick={() => navigate(`/shop?category=${c.slug}`)} className="cursor-pointer focus:bg-[#00F0FF]/10 focus:text-[#00F0FF]" data-testid={`nav-cat-${c.slug}`}>{c.name}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/track" className="rounded-full px-3 py-2 text-sm font-medium text-slate-200 transition-colors hover:text-[#00F0FF]" data-testid="nav-track">Track</Link>
          {["owner", "admin", "manager"].includes(user?.role) && <Link to="/admin" className="rounded-full px-3 py-2 text-sm font-bold text-[#8A2BE2] transition-colors hover:text-[#a56bef]" data-testid="nav-admin">Admin</Link>}
        </nav>

        <div className="ml-auto hidden max-w-xs flex-1 md:block">
          <SearchBar onSubmit={doSearch} testid="header-search" />
        </div>

        <div className="ml-auto flex items-center gap-2 md:ml-0">
          <CurrencySelector />

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="account-menu-btn" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-200 hover:border-[#00F0FF]/40 hover:text-[#00F0FF]">
                  <User className="h-4.5 w-4.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 border-white/10 bg-[#111420] text-slate-200">
                <DropdownMenuLabel className="truncate text-xs text-slate-400">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => navigate("/account")} className="cursor-pointer focus:bg-white/5" data-testid="menu-account"><Package className="mr-2 h-4 w-4" /> My Account</DropdownMenuItem>
                {["owner", "admin", "manager"].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer text-[#00F0FF] focus:bg-[#00F0FF]/10" data-testid="menu-admin"><LayoutDashboard className="mr-2 h-4 w-4" /> Admin Panel</DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={logout} className="cursor-pointer text-[#FF2A6D] focus:bg-[#FF2A6D]/10" data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" /> Sign Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button onClick={() => setAuthOpen(true)} data-testid="header-login-btn" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-200 hover:border-[#00F0FF]/40 hover:text-[#00F0FF]">
              <User className="h-4.5 w-4.5" />
            </button>
          )}

          <button onClick={() => setOpen(true)} data-testid="cart-toggle-btn" className="relative flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-200 hover:border-[#00F0FF]/40 hover:text-[#00F0FF]">
            <ShoppingCart className="h-4.5 w-4.5" />
            {count > 0 && (
              <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#00F0FF] px-1 text-[10px] font-bold text-[#08090E]" data-testid="cart-count-badge">
                {count}
              </motion.span>
            )}
          </button>
        </div>
      </div>
      <div className="border-t border-white/10 px-4 py-2 md:hidden">
        <SearchBar onSubmit={doSearch} testid="mobile-search" />
      </div>
    </header>
  );
}
